/**
 * 영상 소스 파서
 * - youtube_link 컬럼에는 이제 모든 종류의 영상 URL 이 저장될 수 있다.
 *   (유튜브 전체 URL, 레거시 v=ID, 영상 파일(.mp4 등), 임베드 가능한 기타 URL)
 * - URL 을 분석하여 재생 방식(playType), 임베드 URL, 자동 썸네일 등을 계산한다.
 */

/**
 * YouTube URL 또는 videoId 에서 11자리 videoId 추출
 * (기존 videoListMixin.js 의 로직을 그대로 사용)
 * @param {string} url
 * @returns {string} 11자리 videoId 또는 빈 문자열
 */
function extractYouTubeVideoId(url) {
  if (!url) return '';

  // 이미 videoId(11자리)만 있는 경우
  if (url.length === 11 && !url.includes('/') && !url.includes('=')) {
    return url;
  }

  // URL 에서 videoId 추출
  const patterns = [
    /(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([a-zA-Z0-9_-]{11})/,
    /v=([a-zA-Z0-9_-]{11})/,
    /\/([a-zA-Z0-9_-]{11})$/
  ];

  for (const pattern of patterns) {
    const match = url.match(pattern);
    if (match && match[1]) {
      return match[1];
    }
  }

  return '';
}

const VIDEO_FILE_EXTENSIONS = ['.mp4', '.webm', '.ogg', '.ogv', '.mov'];

/**
 * 영상 소스 정보를 파싱한다.
 * @param {string} url
 * @returns {{ playType: 'iframe'|'video', embedUrl: string, youtubeId: string, autoThumbnail: string }}
 */
export function parseVideoSource(url) {
  if (!url) {
    return { playType: 'iframe', embedUrl: '', youtubeId: '', autoThumbnail: '' };
  }

  // 1) 유튜브
  const youtubeId = extractYouTubeVideoId(url);
  if (youtubeId) {
    return {
      playType: 'iframe',
      embedUrl: `https://www.youtube.com/embed/${youtubeId}?autoplay=1`,
      youtubeId: youtubeId,
      autoThumbnail: `https://i.ytimg.com/vi/${youtubeId}/hqdefault.jpg`
    };
  }

  // 2) 영상 파일 (확장자로 판별, query/hash 는 제외)
  const pathOnly = String(url).split(/[?#]/)[0].toLowerCase();
  const isVideoFile = VIDEO_FILE_EXTENSIONS.some((ext) => pathOnly.endsWith(ext));
  if (isVideoFile) {
    return { playType: 'video', embedUrl: url, youtubeId: '', autoThumbnail: '' };
  }

  // 3) 그 외 임베드 가능한 일반 URL → iframe 으로 그대로 사용
  return { playType: 'iframe', embedUrl: url, youtubeId: '', autoThumbnail: '' };
}

export default parseVideoSource;
